// AI Generated Test
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { ChangesHistoryService } from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { PropertiesSumService } from '@client/app/filters/properties-sum.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { SeoRepoService } from '@client/app/services/api/seo-repo/seo-repo.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';

@Component({
    selector: 'domain-dashboard-seo',
    standalone: false,
    template: `
        <div class="card panel-monsido-card max-height d-flex flex-column">
            <a class="p-0" uiSref="base.customer.domain.seo.summary" [attr.aria-label]="'SEO Issues' | translate">
                <mon-panel-header [pHeader]="'Search Engine Optimization (SEO)' | translate">
                    <div class="text-right">
                        <div class="btn btn-link">
                            <span class="pl-1 fas fa-chevron-right" aria-hidden="true"></span>
                        </div>
                    </div>
                </mon-panel-header>
            </a>
            <div class="card-body flex-grow-1">
                <mon-spinner [loading]="loading">
                    <div class="row my-3">
                        <div class="offset-4 col-18">
                            <mon-chart-doughnut [labels]="doughnutLabels"
                                            [data]="doughnutData"
                                            [options]="doughnutOptions"
                                            [colors]="colors">
                            </mon-chart-doughnut>
                        </div>
                        <div class="col-26">
                            <div class="row">
                                <div class="vcenter col-48 mb-4 px-0">
                                    <notification-change [tooltipTitle]="'Contains high, medium & low opportunities for all pages' | translate"
                                                         [title]="'SEO opportunities' | translate"
                                                         [digit]="seoErrors"
                                                         link="base.customer.domain.seo.summary"
                                                         [miniDigits]="changesSinceLastScan"
                                                         unit="">
                                    </notification-change>
                                </div>
                                <div class="col-48">
                                    <ul class="list-inline">
                                        <li [triggers]="'focus hover'" [ngbTooltip]="'High impact opportunities' | translate">
                                            <a uiSref="base.customer.domain.seo.issues"
                                               class="text-black"
                                               [attr.aria-label]="'Go to checkpoints' | translate">
                                                <span class="fas fa-chart-line fa-lg"
                                                      aria-hidden="true"
                                                      [ngClass]="{'text-danger': seoCategoryErrors.alert > 0, 'text-grey': seoCategoryErrors.alert === 0}">
                                                </span>

                                                <span class="sr-only" translate>High impact opportunities</span>

                                                <span>{{seoCategoryErrors.alert | monNumber}}</span>
                                            </a>
                                        </li>
                                        <li [triggers]="'focus hover'" [ngbTooltip]="'Medium impact opportunities' | translate">
                                            <a uiSref="base.customer.domain.seo.issues"
                                               class="text-black"
                                               [attr.aria-label]="'Go to checkpoints' | translate">
                                                <span class="fas fa-chart-line fa-lg"
                                                      aria-hidden="true"
                                                      [ngClass]="{'text-warning': seoCategoryErrors.medium > 0, 'text-grey': seoCategoryErrors.medium === 0}">
                                                </span>
                                                <span class="sr-only" translate>Medium impact opportunities</span>
                                                <span>{{seoCategoryErrors.medium | monNumber}}</span>
                                            </a>
                                        </li>
                                        <li [triggers]="'focus hover'" [ngbTooltip]="'Low impact opportunities' | translate">
                                            <a uiSref="base.customer.domain.seo.issues"
                                               class="text-black"
                                               [attr.aria-label]="'Go to checkpoints' | translate">
                                                <span class="fas fa-chart-line fa-lg"
                                                      aria-hidden="true"
                                                      [ngClass]="{'text-info': seoCategoryErrors.low > 0, 'text-grey': seoCategoryErrors.low === 0}"></span>
                                                <span class="sr-only" translate>Low impact opportunities</span>
                                                <span>{{seoCategoryErrors.low | monNumber}}</span>
                                            </a>
                                        </li>
                                        <li [triggers]="'focus hover'" [ngbTooltip]="'Technical SEO Issues' | translate">
                                            <a uiSref="base.customer.domain.seo.issues"
                                               class="text-black"
                                               [attr.aria-label]="'Go to checkpoints' | translate">
                                                <span class="fas fa-chart-line fa-lg"
                                                      aria-hidden="true"
                                                      [ngClass]="{'text-primary': seoCategoryErrors.technical > 0,
                                                      'text-grey': seoCategoryErrors.technical === 0}"></span>
                                                <span class="sr-only" translate>Technical SEO Issues</span>
                                                <span>{{seoCategoryErrors.technical | monNumber}}</span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </mon-spinner>
            </div>
              <div class="card-footer bg-white text-right" *ngIf="showHistory">
                    <a class="btn-link" uiSref="base.customer.domain.changesHistory" [uiParams]="historyCenterParameters" translate>
                        Show history
                    </a>
            </div>
        </div>
    `,
})
export class DomainDashboardSeoComponent implements OnInit {
    @Input() historyData: any;

    historyCenterParameters: any = {};
    loading = true;
    doughnutLabels: string[] = ['', ''];
    colors: string[] = ['#4dc0bd', '#dcdcdc'];
    showHistory = false;
    total = 0;
    totalPages = 0;
    domain: any;
    seoCategoryErrors: any = {};
    doughnutData: any;
    doughnutOptions: any;
    seoErrors = 0;
    changesSinceLastScan: any;

    constructor (
        private seoRepoService: SeoRepoService,
        private pipeService: PipeService,
        private translateService: TranslateService,
        private complianceService: ComplianceService,
        private sessionService: SessionService,
        private changesHistoryService: ChangesHistoryService,
        private activeFeatureService: ActiveFeatureService,
        private propertiesSumService: PropertiesSumService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    private activate (): void {
        this.seoCategoryErrors = {};
        this.loading = true;
        this.doughnutLabels = ['', ''];
        this.colors = ['#4dc0bd', '#dcdcdc'];
        this.historyCenterParameters = this.changesHistoryService.getHistoryCenterParameters('seo');
        this.showHistory = this.activeFeatureService.isFeatureActivePure('history_center_all');
        this.total = 0;
        this.totalPages = 0;
        this.domain = this.sessionService.domain;
        const promises = [this.getSummary(), this.getCheckpoints()];
        Promise.all(promises).then((values: any[]) => {
            this.setupDoughnutChart(values[0], values[1]);
            this.loading = false;
        }).catch(() => {
            this.loading = false;
        });
    }

    private getSummary (): Promise<any> {
        return this.seoRepoService.getSummary();
    }

    private getCheckpoints (): Promise<any> {
        const params: any = {
            page_size: 0,
        };
        return this.seoRepoService.getAll(params);
    }

    private setupDoughnutChart (summary: any, checkpoints: any): void {
        const percentage = this.complianceService.seoCompliancePercentage(summary, checkpoints, this.historyData);
        this.doughnutData = this.complianceService.seoCompliance(summary, checkpoints, this.historyData);
        this.doughnutOptions = {
            centerText: {
                percentageLabel: this.pipeService.get('monNumber')(percentage / 100, '0.[00] %'),
                totalLabel: this.translateService.getString('Overall compliance'),
                subLabel: this.translateService.getString('achieved'),
                totalFontSize: 150,
                subFontSize: 150,
            },
            cutoutPercentage: 80,
            tooltips: {
                enabled: false,
            },
            hover: {
                mode: null,
            },
        };
        this.seoCategoryErrors.alert = this.propertiesSumService.sum(summary, ['seo_errors_alerts_count']);
        this.seoCategoryErrors.low = this.propertiesSumService.sum(summary, ['seo_errors_infos_count']);
        this.seoCategoryErrors.medium = this.propertiesSumService.sum(summary, ['seo_errors_warnings_count']);
        this.seoCategoryErrors.technical = this.propertiesSumService.sum(summary, ['seo_errors_technicals_count']);
        this.seoErrors = this.seoCategoryErrors.alert + this.seoCategoryErrors.low + this.seoCategoryErrors.medium;
    }
}
